import graphql__ff12b1c5a7ea370b3d1f from "./__generated__/AnalysisForecastPartsPanel_part.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import {readInlineData} from "react-relay/hooks";
import {useIntl} from "react-intl";
import {Link} from "react-router";
import {faXmark} from "@fortawesome/pro-regular-svg-icons";
import Panel from "./presentational/Panel";
import Table from "./presentational/table/Table";
import TableHeader, {TextAlignment} from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import ActionButton from "./presentational/button/ActionButton";
import {expenseInvoicesPath} from "../helper/expenseInvoiceFilter";
import {ExpenseCategory} from "../helper/expenseLabels";
import {formatDay, monthToDate} from "../helper/dateFormat";
import {formatAmount} from "../helper/revenueFormat";
import type {AnalysisForecastPartsPanel_part$key, AnalysisForecastPartsPanel_part$data} from "./__generated__/AnalysisForecastPartsPanel_part.graphql";

type Part = AnalysisForecastPartsPanel_part$data;

/** A cell of the result or the liquidity table with the parts it consists of */
export type ForecastLine = {
    amount: number
    parts: Part[]
};

/**
 * A part of a cell of the result or the liquidity table. The places of the forecast query that
 * select lines share this fragment.
 */
const partFragment = graphql__ff12b1c5a7ea370b3d1f;

/**
 * Reads the parts of a line selected with the fragment, keeping the other fields of the line.
 */
export const readForecastLine = <L extends {readonly amount: number, readonly parts: readonly AnalysisForecastPartsPanel_part$key[]}>(line: L): Omit<L, 'parts'> & ForecastLine => ({
    ...line,
    parts: line.parts.map(part => readInlineData(partFragment, part)),
});

const PART_KIND_ORDER: Part['kind'][] = ['BWA', 'INVOICES', 'EXPENSE_SUPPLIER', 'BANK_BOOKINGS', 'SUBSCRIPTION', 'OPEN_INVOICE', 'SUPPLIER', 'PLANNED_ITEM', 'VAT_PAYMENT'];

/** The suffix of the translation id of the BWA column of a figure */
const BWA_FIELD_COLUMNS: Record<string, string> = {
    REVENUE: 'revenue',
    OTHER_OPERATING_INCOME: 'otherOperatingIncome',
    PERSONNEL_COSTS: 'personnelCosts',
    HOSTING_AND_LICENCES: 'hostingAndLicences',
    OTHER_COSTS: 'otherCosts',
    DEPRECIATION: 'depreciation',
    INCOME_TAXES: 'incomeTaxes',
};

type PartRow = {
    key: string
    part: Part | null // null for the total of an expense category
    label: React.ReactNode
    link: string | null
    date: string | null
    netAmount: number
    vatAmount: number
    grossAmount: number
    isIndented: boolean
    isSubtotal: boolean
};

type PartsPanelProps = {
    title: string
    line: ForecastLine
    month: string
    isResult: boolean
    lastBookingDay: string
    onClose: () => void
};

/**
 * The parts a cell consists of, grouped by their kind. The amount the table shows is bold: net
 * for the result, gross for the liquidity. In the result, the expected supplier amounts are listed
 * with the incoming invoices of their expense category and marked as expected or estimated.
 */
const AnalysisForecastPartsPanel: React.FC<PartsPanelProps> = ({title, line, month, isResult, lastBookingDay, onClose}) => {
    const intl = useIntl();

    const groupKindOf = (part: Part): Part['kind'] => isResult && part.kind === 'SUPPLIER' ? 'EXPENSE_SUPPLIER' : part.kind;
    const groups = PART_KIND_ORDER
        .map(kind => ({kind, parts: line.parts.filter(part => groupKindOf(part) === kind)}))
        .filter(group => group.parts.length > 0);

    // An amount from an estimate source instead of the last invoices, such as the Hetzner estimate
    const estimateLabel = (estimate: NonNullable<Part['supplierAmountEstimate']>): string => intl.formatMessage(
        {id: `view.analysisForecastOverview.parts.estimate.${estimate.origin}`},
        {
            source: intl.formatMessage({id: `view.analysisForecastOverview.parts.estimateSource.${estimate.source}`}),
            date: estimate.estimatedAt ? intl.formatDate(new Date(estimate.estimatedAt), {day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'}) : '–',
        }
    );

    const partLabel = (part: Part): React.ReactNode => {
        switch (part.kind) {
            case 'BWA':
                return part.bwaField ? intl.formatMessage({id: `view.analysisForecastBwa.col.${BWA_FIELD_COLUMNS[part.bwaField] ?? part.bwaField}`, defaultMessage: part.bwaField}) : '';
            case 'INVOICES':
                return (
                    <>
                        {part.label}
                        <span className="ml-2 text-xs text-gray-500">{intl.formatMessage({id: 'view.analysisForecastOverview.parts.invoicesLabel'}, {count: part.count ?? 0})}</span>
                    </>
                );
            case 'BANK_BOOKINGS':
                return intl.formatMessage({id: 'view.analysisForecastOverview.parts.bankBookingsLabel'}, {date: formatDay(intl, lastBookingDay)});
            case 'VAT_PAYMENT':
                return part.vatPayable
                    ? intl.formatMessage({id: 'view.analysisForecastOverview.parts.vatPaymentLabel'}, {month: intl.formatDate(monthToDate(part.vatPayable.month), {month: 'long', year: 'numeric', timeZone: 'UTC'})})
                    : '';
            default:
                return (
                    <>
                        {part.label ?? ''}
                        {part.supplierAmountEstimate && (
                            <span className="ml-2 text-xs text-gray-500">{estimateLabel(part.supplierAmountEstimate)}</span>
                        )}
                        {isResult && part.kind === 'SUPPLIER' && !part.supplierAmountEstimate && (
                            <span className="ml-2 text-xs text-gray-500">{intl.formatMessage({id: 'view.analysisForecastOverview.parts.expected'})}</span>
                        )}
                        {part.isPostponedToNextMonth && (
                            <span className="ml-2 text-xs text-gray-500">{intl.formatMessage({id: 'view.analysisForecastOverview.parts.postponed'})}</span>
                        )}
                    </>
                );
        }
    };

    const expenseCategoryLabel = (expenseCategory: Part['expenseCategory']) => expenseCategory
        ? intl.formatMessage({id: `expense.category.${expenseCategory}`, defaultMessage: expenseCategory})
        : intl.formatMessage({id: 'view.analysisExpenseOverview.unassigned'});

    const expenseCategoryLink = (expenseCategory: Part['expenseCategory']): string | null =>
        isResult ? expenseInvoicesPath({category: expenseCategory ? expenseCategory as ExpenseCategory : 'NONE', month}) : null;

    const partLink = (part: Part): string | null => {
        if (part.sourceIdentifier === null || part.sourceIdentifier === undefined) {
            return null;
        }
        switch (part.kind) {
            case 'INVOICES':
                return `/customers/${part.sourceIdentifier}`;
            case 'EXPENSE_SUPPLIER':
                return isResult ? expenseInvoicesPath({supplier: part.sourceIdentifier, month}) : null;
            case 'SUPPLIER':
                return expenseInvoicesPath({supplier: part.sourceIdentifier});
            case 'SUBSCRIPTION':
                return `/subscriptions/${part.sourceIdentifier}`;
            case 'OPEN_INVOICE':
                return `/invoices/${part.sourceIdentifier}`;
            case 'PLANNED_ITEM':
                return '/analysis/forecast/planned-items';
            default:
                return null;
        }
    };

    const componentLabel = (component: NonNullable<Part['vatPayable']>['components'][number]) => {
        if (component.kind === 'OUTGOING_INVOICES' || component.kind === 'INCOMING_INVOICES') {
            return intl.formatMessage({id: `view.analysisForecastOverview.parts.vat.${component.kind}`}, {count: component.count ?? 0});
        }
        return component.label ?? '';
    };

    const componentLink = (component: NonNullable<Part['vatPayable']>['components'][number]): string | null => {
        if (component.sourceIdentifier === null || component.sourceIdentifier === undefined) {
            return component.kind === 'PLANNED_ITEM' ? '/analysis/forecast/planned-items' : null;
        }
        switch (component.kind) {
            case 'EXPECTED_SUBSCRIPTION':
                return `/subscriptions/${component.sourceIdentifier}`;
            case 'EXPECTED_SUPPLIER':
                return expenseInvoicesPath({supplier: component.sourceIdentifier});
            case 'PLANNED_ITEM':
                return '/analysis/forecast/planned-items';
            default:
                return null;
        }
    };

    /**
     * The rows of a group of parts. The suppliers of an expense category are listed indented below
     * a row with the total of the category.
     */
    const rowsOf = (kind: Part['kind'], parts: Part[]): PartRow[] => {
        if (kind !== 'EXPENSE_SUPPLIER') {
            return parts.map((part, index) => ({
                key: `${part.kind}-${part.sourceIdentifier ?? ''}-${index}`,
                part,
                label: partLabel(part),
                link: partLink(part),
                date: part.date ?? null,
                netAmount: part.netAmount,
                vatAmount: part.vatAmount,
                grossAmount: part.grossAmount,
                isIndented: false,
                isSubtotal: false,
            }));
        }
        const partsByCategory = new Map<string, Part[]>();
        parts.forEach(part => partsByCategory.set(part.expenseCategory ?? '', [...(partsByCategory.get(part.expenseCategory ?? '') ?? []), part]));
        return Array.from(partsByCategory.entries()).flatMap(([categoryKey, categoryParts]) => {
            const expenseCategory = categoryParts[0].expenseCategory;
            return [
                {
                    key: `category-${categoryKey}`,
                    part: null,
                    label: expenseCategoryLabel(expenseCategory),
                    link: expenseCategoryLink(expenseCategory),
                    date: null,
                    netAmount: categoryParts.reduce((sum, part) => sum + part.netAmount, 0),
                    vatAmount: categoryParts.reduce((sum, part) => sum + part.vatAmount, 0),
                    grossAmount: categoryParts.reduce((sum, part) => sum + part.grossAmount, 0),
                    isIndented: false,
                    isSubtotal: true,
                },
                ...categoryParts.map((part, index) => ({
                    key: `supplier-${categoryKey}-${part.sourceIdentifier ?? ''}-${index}`,
                    part,
                    label: partLabel(part),
                    link: partLink(part),
                    date: part.date ?? null,
                    netAmount: part.netAmount,
                    vatAmount: part.vatAmount,
                    grossAmount: part.grossAmount,
                    isIndented: true,
                    isSubtotal: false,
                })),
            ];
        });
    };

    // A result drill-down shows the net amounts only, the VAT belongs to the liquidity
    const hasDates = line.parts.some(part => part.date);
    const columnCount = (hasDates ? 1 : 0) + (isResult ? 2 : 4);

    const netSum = line.parts.reduce((sum, part) => sum + part.netAmount, 0);
    const vatSum = line.parts.reduce((sum, part) => sum + part.vatAmount, 0);
    const grossSum = line.parts.reduce((sum, part) => sum + part.grossAmount, 0);

    const emphasis = (isMain: boolean) => isMain ? 'font-semibold text-gray-900' : 'text-gray-600';
    const amountClass = (amount: number, isMain: boolean) => `${amount < 0 ? 'text-red-600' : ''} ${amount < 0 && isMain ? 'font-semibold' : emphasis(isMain)}`;
    const amountCell = (amount: number, isMain: boolean, className = 'px-3') => (
        <TableData textAlignment={TextAlignment.Right} className={`tabular-nums ${className}`}>
            <span className={amountClass(amount, isMain)}>{formatAmount(amount)}</span>
        </TableData>
    );

    return (
        <Panel
            headline={title}
            fullWidthChildren
            buttons={
                <ActionButton icon={faXmark} title={intl.formatMessage({id: 'view.analysisForecastOverview.parts.close'})} onClick={onClose} additionalClassname=""/>
            }
        >
            <div className="overflow-x-auto">
                <Table>
                    <thead>
                        <tr>
                            <TableHeader className="pl-4 pr-3 sm:pl-6"/>
                            {hasDates && <TableHeader className="px-3">{intl.formatMessage({id: isResult ? 'view.analysisForecastOverview.parts.expectedPayment' : 'view.analysisForecastOverview.parts.paymentDay'})}</TableHeader>}
                            <TableHeader className={isResult ? 'pl-3 pr-4 sm:pr-6' : 'px-3'} textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastOverview.parts.net'})}</TableHeader>
                            {!isResult && <TableHeader className="px-3" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastOverview.parts.vat'})}</TableHeader>}
                            {!isResult && <TableHeader className="pl-3 pr-4 sm:pr-6" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.analysisForecastOverview.parts.gross'})}</TableHeader>}
                        </tr>
                    </thead>
                    {groups.map(group => (
                        <tbody key={group.kind}>
                            <tr className="bg-gray-50">
                                <td colSpan={columnCount} className="py-1.5 pl-4 pr-3 text-xs font-medium uppercase tracking-wide text-gray-500 sm:pl-6">
                                    {intl.formatMessage({id: `view.analysisForecastOverview.parts.kind.${group.kind}`})}
                                </td>
                            </tr>
                            {rowsOf(group.kind, group.parts).map(row => (
                                <React.Fragment key={row.key}>
                                    <tr>
                                        <TableData className={`pr-3 ${row.isIndented ? 'pl-8 sm:pl-10' : 'pl-4 sm:pl-6'}`}>
                                            <span className={row.isSubtotal ? 'font-medium text-gray-900' : undefined}>
                                                {row.link ? <Link className="text-blue-600 hover:underline" to={row.link}>{row.label}</Link> : row.label}
                                            </span>
                                        </TableData>
                                        {hasDates && <TableData className="px-3 tabular-nums text-gray-500">{row.date ? formatDay(intl, row.date) : ''}</TableData>}
                                        {amountCell(row.netAmount, isResult, isResult ? 'pl-3 pr-4 sm:pr-6' : 'px-3')}
                                        {!isResult && amountCell(row.vatAmount, false)}
                                        {!isResult && amountCell(row.grossAmount, true, 'pl-3 pr-4 sm:pr-6')}
                                    </tr>
                                    {row.part?.vatPayable && (
                                        <tr>
                                            <td colSpan={columnCount} className="pb-2 pl-8 pr-4 text-xs text-gray-500 sm:pl-10">
                                                <div className="mb-1">
                                                    {intl.formatMessage({id: 'view.analysisForecastOverview.parts.vat.header'}, {
                                                        month: intl.formatDate(monthToDate(row.part.vatPayable.month), {month: 'long', year: 'numeric', timeZone: 'UTC'}),
                                                        date: formatDay(intl, row.part.vatPayable.paymentDate),
                                                    })}
                                                </div>
                                                <ul className="space-y-0.5">
                                                    {row.part.vatPayable.components.map((component, componentIndex) => (
                                                        <li key={componentIndex} className="flex justify-between gap-4 sm:max-w-xl">
                                                            <span>
                                                                {componentLink(component)
                                                                    ? <Link className="text-blue-600 hover:underline" to={componentLink(component) as string}>{componentLabel(component)}</Link>
                                                                    : componentLabel(component)}
                                                            </span>
                                                            <span className={`tabular-nums ${component.vatAmount < 0 ? 'text-red-600' : ''}`}>{formatAmount(component.vatAmount)}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    ))}
                    <tfoot>
                        <tr className="border-t border-gray-300">
                            <TableData className="pl-4 pr-3 sm:pl-6"><span className="font-semibold text-gray-900">{intl.formatMessage({id: 'view.analysisForecastOverview.parts.sum'})}</span></TableData>
                            {hasDates && <TableData/>}
                            {amountCell(netSum, isResult, isResult ? 'pl-3 pr-4 sm:pr-6' : 'px-3')}
                            {!isResult && amountCell(vatSum, false)}
                            {!isResult && amountCell(grossSum, true, 'pl-3 pr-4 sm:pr-6')}
                        </tr>
                    </tfoot>
                </Table>
            </div>
        </Panel>
    );
};

export default AnalysisForecastPartsPanel;
